import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { UseQueryParameters } from '~/types/ApiRequestTypes'

import {
  ApiError,
  PaginatedApiResult,
  User,
  UsersQueryParams,
} from '@mangovoice/types'

interface UseGetPbxUsersParams
  extends UseQueryParameters<PaginatedApiResult<User>> {
  pbxId: number
  _queryParams: UsersQueryParams
}

export const useGetPbxUsers = ({
  pbxId,
  onQueryStarted,
  onQuerySuccess,
  onQueryCancelled,
  onQueryError,
  options,
  _queryParams,
}: UseGetPbxUsersParams) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()
  const [queryParams, setQueryParams] = useState<UsersQueryParams>(_queryParams)

  const getQueryKey = () => {
    const key = ['users', pbxId, { ...queryParams }]
    return key
  }

  const query = useQuery<PaginatedApiResult<User>, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (pbxId === 0) {
        return Promise.reject({ message: 'pbxId is required' })
      }

      const params = {
        ...queryParams,
        pbx_ids: [pbxId],
      } as UsersQueryParams

      const { ok, data, status } = await api.getPbxUsers(params, signal)

      if (!ok || !data) {
        const error = {
          ...data,
          status,
        }
        if (!signal?.aborted) {
          onQueryError?.(error)
        } else {
          onQueryCancelled?.()
        }
        return Promise.reject(error)
      }

      onQuerySuccess?.(data)
      return data
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: options?.retry ?? false,
  })

  const tryCancelQuery = () => {
    const queryKey = getQueryKey()
    queryClient.cancelQueries({
      queryKey,
    })
  }

  const refetch = useCallback(
    (newParams?: UsersQueryParams) => {
      tryCancelQuery()
      if (newParams) {
        setQueryParams(newParams)
      }
      query.refetch()
    },
    [query],
  )

  const results = useMemo(() => {
    return query.data?.results
  }, [query.data])

  const totalCount = useMemo(() => {
    return query.data?.count
  }, [query.data])

  useEffect(() => {
    query.refetch()
  }, [queryParams, pbxId])

  return {
    ...query,
    results,
    totalCount,
    refetch,
    tryCancelQuery,
  }
}
